import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, LogOut, Globe, Network, Receipt, FolderKanban, Users, ShieldCheck } from 'lucide-react';
import {
  CommandPalette,
  CommandGroup,
  CommandItem,
} from '@vnetwork/ui/primitives';
import { useTheme } from '@vnetwork/ui/provider';
import { useAuth } from '@/auth/AuthProvider';
import { useCdnUmbrellas } from '@/api/manticore/cdn-providers';
import { useDomainListPaged } from '@/api/manticore/domains';
import { useMyServices, serviceLabel, serviceProviderScope } from '@/api/services';
import { useInvoices } from '@/api/billing';
import { useIamProjects, useIamTeams } from '@/api/iam';
import { useServiceWaapDomainsPaged } from '@/api/manticore/service-waap';
import { cdnProviderLabel } from '@vnetwork/margarita-core/services';
import { domainStatusLabel } from '@/components/domain-status';
import { serviceRoute, iconForScope, categoryOfScope } from '@/lib/service-categories';
import { paths } from '@/lib/paths';
import { formatCurrency } from '@vnetwork/margarita-core/utils';
import { trackSearch } from '@/lib/analytics-events';
import { PRIMARY_NAV } from './nav';

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ⌘K command palette — jump to a section, a CDN orchestrator console, or
 * search domains across the org (domain-first entry for users who think
 * "my domain example.com", not "my service"). All data queries are gated on
 * `open` so the palette costs nothing until summoned.
 */
export function CommandMenu({ open, onOpenChange }: CommandMenuProps) {
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const { logout } = useAuth();
  const [query, setQuery] = useState('');

  // Track search intent, debounced so we log the settled term (not each keystroke).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => trackSearch(query), 800);
    return () => clearTimeout(t);
  }, [query, open]);

  // Orchestrator jump list (cmdk filters client-side; the list is small).
  const { data: umbrellasData } = useCdnUmbrellas(undefined, { enabled: open });
  const umbrellas = umbrellasData?.umbrellas ?? [];

  // Domain search is server-side (org can hold 2000+ domains) — only fires
  // from 2 chars; returned matches contain the query so they pass cmdk's
  // client filter too.
  const domainSearch = query.trim();
  const { data: domainsPage } = useDomainListPaged({
    page: 1,
    perPage: 20,
    search: domainSearch,
    enabled: open && domainSearch.length >= 2,
  });
  const domains = domainSearch.length >= 2 ? (domainsPage?.data ?? []) : [];

  // All palette-only data is gated on `open` so it costs nothing until summoned.
  // Provisioned services (jump to each console) — small org-scoped list, cached.
  const { data: myServices = [] } = useMyServices({ enabled: open });
  // Recent invoices — only surfaced while searching (not a useful empty jump list).
  const { data: invoicesData } = useInvoices(1, 8, { enabled: open });
  // Projects / teams — small org-scoped lists, surfaced while searching.
  const { data: allProjects = [] } = useIamProjects(undefined, false, { enabled: open });
  const { data: allTeams = [] } = useIamTeams(undefined, { enabled: open });
  // WAAP websites — server-searched under the org's WAAP (Cobra) service, if any.
  const waapServiceId = myServices.find(
    (s) => serviceRoute(serviceProviderScope(s), s.id)?.startsWith(`${paths.waap.base}/`),
  )?.id;
  const { data: waapPage } = useServiceWaapDomainsPaged(waapServiceId ?? '', {
    page: 1,
    perPage: 20,
    search: domainSearch,
    enabled: open && domainSearch.length >= 2 && !!waapServiceId,
  });

  // cmdk's client-side fuzzy filter is turned off (see `shouldFilter` below):
  // it drops async, server-searched domain results that mount after the user
  // has typed. With it off, the palette owns filtering of its STATIC groups
  // here; the domain group is already filtered server-side by `search`.
  const q = query.trim().toLowerCase();
  const matchesQuery = (...text: string[]) =>
    !q || text.some((t) => t.toLowerCase().includes(q));
  const navItems = PRIMARY_NAV.filter((item) => matchesQuery(item.label));
  const orchestrators = umbrellas.filter((u) =>
    matchesQuery(u.service_name, cdnProviderLabel(u.provider_name)),
  );
  // Services jump list — resolve each to its console route; skip CDN (covered by
  // the orchestrators group above) and any scope without a console route.
  const services = myServices
    .map((s) => {
      const scope = serviceProviderScope(s);
      return { s, scope, route: serviceRoute(scope, s.id) };
    })
    .filter((x) => x.route && categoryOfScope(x.scope) !== 'cdn' && matchesQuery(serviceLabel(x.s)));
  // Invoices only when the user is actively searching.
  const invoices = q
    ? (invoicesData?.invoices ?? []).filter((iv) =>
        matchesQuery(iv.invoice_id, iv.invoice_description, iv.invoice_status),
      )
    : [];
  const projects = q ? allProjects.filter((p) => matchesQuery(p.name, p.slug)) : [];
  const teams = q ? allTeams.filter((t) => matchesQuery(t.name)) : [];
  const waapSites = domainSearch.length >= 2 && waapServiceId ? (waapPage?.data ?? []) : [];
  const themeLabel = theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode';
  const showThemeCmd = matchesQuery(themeLabel, 'theme', 'dark', 'light', 'mode');
  const showSignOutCmd = matchesQuery('Sign out', 'logout', 'log out');

  const go = (to: string) => {
    onOpenChange(false);
    setQuery('');
    navigate(to);
  };

  return (
    <CommandPalette
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setQuery('');
      }}
      placeholder="Search domains, services, pages…"
      value={query}
      onValueChange={setQuery}
      // Off: cmdk's client filter otherwise drops async server-searched domain
      // results that mount after the user has typed; the static groups are
      // filtered manually above instead.
      shouldFilter={false}
    >
      {navItems.length > 0 && (
        <CommandGroup heading="Navigation">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.to}
                icon={<Icon size={16} />}
                onSelect={() => go(item.to)}
              >
                {item.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      )}

      {services.length > 0 && (
        <CommandGroup heading="Services">
          {services.map(({ s, scope, route }) => {
            const Ic = iconForScope(scope);
            return (
              <CommandItem
                key={s.id}
                icon={<Ic size={16} />}
                keywords={[serviceLabel(s)]}
                onSelect={() => go(route!)}
              >
                {serviceLabel(s)}
              </CommandItem>
            );
          })}
        </CommandGroup>
      )}

      {projects.length > 0 && (
        <CommandGroup heading="Projects">
          {projects.map((p) => (
            <CommandItem
              key={p.id}
              icon={<FolderKanban size={16} />}
              keywords={[p.name, p.slug]}
              onSelect={() => go(`${paths.organizations.base}/projects/${p.id}`)}
            >
              {p.name}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {teams.length > 0 && (
        <CommandGroup heading="Teams">
          {teams.map((t) => (
            <CommandItem
              key={t.id}
              icon={<Users size={16} />}
              keywords={[t.name]}
              onSelect={() => go(`${paths.organizations.base}/teams/${t.id}`)}
            >
              {t.name}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {orchestrators.length > 0 && (
        <CommandGroup heading="CDN orchestrators">
          {orchestrators.map((u) => (
            <CommandItem
              key={u.service_id}
              icon={<Network size={16} />}
              description={cdnProviderLabel(u.provider_name)}
              keywords={[u.service_name, cdnProviderLabel(u.provider_name)]}
              onSelect={() => go(`/services/cdn/${u.service_id}`)}
            >
              {u.service_name}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {domains.length > 0 && (
        <CommandGroup heading="CDN domains">
          {domains.map((d) => (
            <CommandItem
              key={d.id}
              icon={<Globe size={16} />}
              description={domainStatusLabel(d.status)}
              keywords={[d.domain]}
              onSelect={() => go(paths.raven.domain(d.id))}
            >
              {d.domain}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {waapSites.length > 0 && (
        <CommandGroup heading="WAAP websites">
          {waapSites.map((d) => (
            <CommandItem
              key={d.id}
              icon={<ShieldCheck size={16} />}
              description={domainStatusLabel(d.status)}
              keywords={[d.domain]}
              onSelect={() => go(`${paths.waap.service(waapServiceId)}/websites/${d.id}`)}
            >
              {d.domain}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {invoices.length > 0 && (
        <CommandGroup heading="Invoices">
          {invoices.map((iv) => (
            <CommandItem
              key={iv.invoice_id}
              icon={<Receipt size={16} />}
              description={formatCurrency(iv.invoice_total)}
              keywords={[iv.invoice_id, iv.invoice_description]}
              onSelect={() => go(`/account/billing/invoices/${iv.invoice_id}`)}
            >
              {`Invoice #${iv.invoice_id}`}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {(showThemeCmd || showSignOutCmd) && (
        <CommandGroup heading="Commands">
          {showThemeCmd && (
            <CommandItem
              icon={theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
              description={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
              onSelect={() => {
                setTheme(theme === 'light' ? 'dark' : 'light');
                onOpenChange(false);
              }}
            >
              {themeLabel}
            </CommandItem>
          )}
          {showSignOutCmd && (
            <CommandItem
              icon={<LogOut size={16} />}
              description="End your session"
              onSelect={() => logout()}
            >
              Sign out
            </CommandItem>
          )}
        </CommandGroup>
      )}
    </CommandPalette>
  );
}
